import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const BoxContext = createContext();

export const useBox = () => {
  const context = useContext(BoxContext);
  if (!context) {
    throw new Error('useBox must be used within a BoxProvider');
  }
  return context;
};

export const BoxProvider = ({ children }) => {
  const [boxItems, setBoxItems] = useState([]);
  const [isBoxOpen, setIsBoxOpen] = useState(false);

  // Charger la box depuis le localStorage au démarrage de l'application
  useEffect(() => {
    const savedBox = localStorage.getItem('bpara-box');
    if (savedBox) {
      try {
        setBoxItems(JSON.parse(savedBox));
      } catch (error) {
        console.error('Erreur lors du chargement de la box sauvegardée :', error);
      }
    }
  }, []);

  // Sauvegarder la box dans le localStorage à chaque modification
  useEffect(() => {
    localStorage.setItem('bpara-box', JSON.stringify(boxItems));
  }, [boxItems]);

  // Ajouter un produit à la box
  const addToBox = (product, quantity = 1) => {
    setBoxItems(currentItems => {
      const existingItem = currentItems.find(item => item.id === product.id);
      
      if (existingItem) {
        // Si le produit existe déjà, augmenter la quantité
        const updatedItems = currentItems.map(item =>
          item.id === product.id 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
        toast.success(`Quantité de ${product.name} mise à jour dans votre box !`);
        return updatedItems;
      } else {
        // Ajouter un nouveau produit à la box
        const newItem = {
          ...product,
          quantity,
          addedAt: new Date().toISOString()
        };
        toast.success(`${product.name} ajouté à votre box !`);
        return [...currentItems, newItem];
      }
    });
  };

  // Retirer un produit de la box
  const removeFromBox = (productId) => {
    setBoxItems(currentItems => {
      const itemToRemove = currentItems.find(item => item.id === productId);
      const updatedItems = currentItems.filter(item => item.id !== productId);
      if (itemToRemove) {
        toast.info(`${itemToRemove.name} retiré de votre box`);
      }
      return updatedItems;
    });
  };

  // Mettre à jour la quantité d'un produit
  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromBox(productId);
      return;
    }

    setBoxItems(currentItems =>
      currentItems.map(item =>
        item.id === productId 
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  // Vider la box entièrement
  const clearBox = () => {
    setBoxItems([]);
    toast.info('Votre box a été vidée');
  };

  // Obtenir le nombre total d'articles
  const getTotalItems = () => {
    return boxItems.reduce((total, item) => total + item.quantity, 0);
  };

  // Obtenir le prix total
  const getTotalPrice = () => {
    return boxItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Afficher ou cacher la box
  const toggleBox = () => {
    setIsBoxOpen(!isBoxOpen);
  };

  const value = {
    boxItems,
    isBoxOpen,
    addToBox,
    removeFromBox,
    updateQuantity,
    clearBox,
    getTotalItems,
    getTotalPrice,
    toggleBox,
    setIsBoxOpen
  };

  return (
    <BoxContext.Provider value={value}>
      {children}
    </BoxContext.Provider>
  );
};